/**
 * Centralized Branding Configuration
 *
 * Edit this single file to change ALL branding across the entire dashboard.
 * Colors, logos, and company name are all controlled from here.
 *
 * After editing, the entire app will reflect the new branding automatically.
 */

export interface BrandingConfig {
  company: {
    name: string;
    tagline?: string;
    copyright?: string;
  };
  logos: {
    primary: string;      // Main logo (e.g., "/logo.png")
    wordmark: string;     // Text/wordmark logo (e.g., "/wordmark.svg")
    favicon?: string;     // Optional favicon path
  };
  colors: {
    primary: string;      // Main brand color
    secondary: string;    // Secondary/gradient start color
    tertiary: string;     // Tertiary/gradient end color
    accent: string;       // Accent/highlight color
    ring: string;         // Focus ring color
    gradient: {
      start: string;
      end: string;
      angle: number;      // Gradient angle in degrees
    };
    chart: string[];      // Chart color palette (5+ colors recommended)
    // Page background colors (light and dark mode)
    background?: {
      light: string;      // Background color for light mode (default: #f5f5f5)
      dark: string;       // Background color for dark mode (default: #0a0a0a)
    };
    // Card/panel background colors
    card?: {
      light: string;      // Card background for light mode (default: #ffffff)
      dark: string;       // Card background for dark mode (default: #171717)
    };
  };
  email: {
    headerGradientStart: string;
    headerGradientEnd: string;
    buttonBackground: string;
    accentText: string;
  };
}

/**
 * ============================================
 * EDIT BELOW TO CHANGE BRANDING
 * ============================================
 */
export const branding: BrandingConfig = {
  company: {
    name: "LearnSales",
    tagline: "Sales Analytics Dashboard",
    copyright: "LearnSales",
  },

  logos: {
    primary: "/logo.png",
    wordmark: "/logo.png",
    favicon: "/favicon.ico",
  },

  colors: {
    primary: "#9333EA",      // Purple - main brand color
    secondary: "#C084FC",    // Light purple - gradient start
    tertiary: "#7B2FBE",     // Dark purple - gradient end
    accent: "#DDD6FE",       // Lilac - highlights
    ring: "#9333EA",         // Purple - focus rings

    gradient: {
      start: "#C084FC",      // Light purple
      end: "#7B2FBE",        // Dark purple
      angle: 135,
    },

    chart: [
      "#9333EA",  // Purple (primary)
      "#C084FC",  // Light purple
      "#DDD6FE",  // Lilac
      "#7B2FBE",  // Dark purple
      "#6B21A8",  // Deeper purple
      "#E9D5FF",  // Very light purple
      "#A855F7",  // Mid purple
      "#581C87",  // Very dark purple
    ],

    background: {
      light: "#f5f3ff",   // Very light purple tint for light mode
      dark: "#0A0118",    // Near-black dark purple for dark mode
    },

    card: {
      light: "#ffffff",
      dark: "#1A0533",    // Dark purple card
    },
  },

  email: {
    headerGradientStart: "#C084FC",
    headerGradientEnd: "#7B2FBE",
    buttonBackground: "#9333EA",
    accentText: "#9333EA",
  },
};

// ============================================
// HELPER FUNCTIONS (do not edit)
// ============================================

/**
 * Generate CSS gradient string from config
 */
export function getGradientCSS(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `linear-gradient(${gradient.angle}deg, ${gradient.start} 0%, ${gradient.end} 100%)`;
}

/**
 * Generate Tailwind gradient classes
 * Returns: "from-[#11b2f8] to-[#504df9]"
 */
export function getGradientTailwind(
  gradient: BrandingConfig["colors"]["gradient"] = branding.colors.gradient
): string {
  return `from-[${gradient.start}] to-[${gradient.end}]`;
}

/**
 * Get chart color by index (wraps around if index exceeds array length)
 */
export function getChartColor(index: number): string {
  const chart = branding.colors.chart;
  if (chart.length === 0) return branding.colors.primary;
  return chart[index % chart.length] ?? branding.colors.primary;
}

/**
 * Inject all branding CSS variables into the document root
 * Called automatically by ThemeProvider
 */
export function injectBrandingCSS(): void {
  if (typeof document === "undefined") return;

  const root = document.documentElement;
  const { colors } = branding;

  // Core brand colors
  root.style.setProperty("--brand-primary", colors.primary);
  root.style.setProperty("--brand-secondary", colors.secondary);
  root.style.setProperty("--brand-tertiary", colors.tertiary);
  root.style.setProperty("--brand-accent", colors.accent);
  root.style.setProperty("--brand-ring", colors.ring);

  // Gradient colors
  root.style.setProperty("--brand-gradient-start", colors.gradient.start);
  root.style.setProperty("--brand-gradient-end", colors.gradient.end);

  // Chart colors
  colors.chart.forEach((color, i) => {
    root.style.setProperty(`--chart-${i + 1}`, color);
  });

  // Background colors (with defaults)
  const bgLight = colors.background?.light ?? "#f5f5f5";
  const bgDark = colors.background?.dark ?? "#0a0a0a";
  const cardLight = colors.card?.light ?? "#ffffff";
  const cardDark = colors.card?.dark ?? "#171717";

  root.style.setProperty("--brand-bg-light", bgLight);
  root.style.setProperty("--brand-bg-dark", bgDark);
  root.style.setProperty("--brand-card-light", cardLight);
  root.style.setProperty("--brand-card-dark", cardDark);

  // Also update the semantic color variables
  root.style.setProperty("--primary", colors.primary);
  root.style.setProperty("--ring", colors.ring);

  // Update background based on current theme
  const isDark = root.classList.contains("dark");
  root.style.setProperty("--background", isDark ? bgDark : bgLight);
  root.style.setProperty("--card", isDark ? cardDark : cardLight);
}

/**
 * Get the MongoDB collection prefix based on company name
 * Converts "SalesGrid" to "salesgrid_", "My Company" to "my_company_"
 */
export function getCollectionPrefix(): string {
  return branding.company.name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "") + "_";
}

/**
 * Get a prefixed collection name
 * Example: getCollectionName("users") returns "salesgrid_users"
 */
export function getCollectionName(baseName: string): string {
  return getCollectionPrefix() + baseName;
}

/**
 * Get a prefixed model name (PascalCase)
 * Example: getModelName("User") returns "SalesGrid_User"
 */
export function getModelName(baseName: string): string {
  const prefix = branding.company.name.replace(/[^a-zA-Z0-9]+/g, "");
  return `${prefix}_${baseName}`;
}
